/*
  Referral application form behaviour.

  Four jobs:
  1. Stash the applicant's name/email before submit, so that after Shopify
     re-renders the page we can prefill Calendly and they do not retype it.
  2. Forward UTM parameters through to the booking, so a partner-sourced
     click is still attributable once the visitor leaves the storefront.
  3. Hand off to the calendar after submitting, but leave the visitor in
     control: a real delay, a cancel button, and a one-shot guard so pressing
     Back from Calendly does not bounce them forward again.
  4. Stop a double submit creating two leads.
*/
(function () {
  const STORAGE_KEY = 'arcady:referral-applicant';
  const HANDOFF_KEY = 'arcady:referral-handoff';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  /*
    A type=url input demands a scheme, so "www.brand.com" and "brand.com" are
    both rejected outright with a terse browser bubble. Almost nobody types
    "https://" by hand, so that would fail real applicants at the last step.

    Rather than loosen the field to plain text and accept junk, the value is
    normalised the moment the visitor leaves it: anything that looks like a
    domain gets https:// in front, everything else is left alone so the native
    validation can still object to genuine nonsense.
  */
  const HAS_SCHEME = /^[a-z][a-z0-9+.\-]*:\/\//i;
  // host + at least one dot + a 2-plus letter label, then end, port, path,
  // query or fragment. Matches brand.com, www.brand.com, a.b.brand.co.uk,
  // brand.com/deck, brand.com:8080. Rejects "brand", "a b", "me@brand.com".
  const LOOKS_LIKE_DOMAIN = /^[^\s/?#@]+\.[a-z]{2,}(?:[:/?#]|$)/i;

  function normaliseUrl(input) {
    if (!input) return;
    const value = input.value.trim();
    if (!value) {
      input.value = '';
      return;
    }
    if (HAS_SCHEME.test(value)) {
      input.value = value;
      return;
    }
    // Protocol-relative, e.g. //brand.com
    if (value.slice(0, 2) === '//') {
      input.value = 'https:' + value;
      return;
    }
    if (LOOKS_LIKE_DOMAIN.test(value)) {
      input.value = 'https://' + value;
      return;
    }
    input.value = value;
  }

  function initUrlFields(form) {
    form.querySelectorAll('input[type="url"]').forEach((input) => {
      // Blur covers clicking Submit, which moves focus off the field first.
      input.addEventListener('blur', () => normaliseUrl(input));
      // Enter submits without blurring, so normalise before validation runs.
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') normaliseUrl(input);
      });
    });
  }

  /*
    Calendly prefills its custom ("additional") questions positionally: a1 is
    the first custom question on the event type, a2 the second, and so on. The
    built-in Name, Email and Guests fields are NOT counted -- they have their
    own reserved `name` and `email` parameters.

    ANSWER_FIELDS must therefore match the order of the custom questions on the
    Calendly event exactly. If that order changes, this array changes with it.
    ANSWER_OFFSET exists so an off-by-N is a one-line correction rather than a
    rewrite.
  */
  const ANSWER_OFFSET = 1;
  const ANSWER_FIELDS = [
    'company',      // a1  Company Name
    'companyDesc',  // a2  Company Description
    'website',      // a3  Business Website
    'category',     // a4  Business Category
    'services',     // a5  Product / Services Description
    'deck',         // a6  Pitch Deck or One Pager
    'phone',        // a7  Phone Number
  ];
  const STASH_FIELDS = ['name', 'email'].concat(ANSWER_FIELDS);

  /*
    Shopify's contact form emails the submission and stores nothing in the
    admin, so an unwatched inbox loses applicants permanently. Posting the same
    data to a webhook alongside the native submit gives a durable, structured
    record without giving up Shopify's email or its spam protection.

    Keys are flat snake_case because that is what a Zapier "Catch Hook" turns
    into individually mappable fields. Every key is always present, using an
    empty string for blanks: a key that is sometimes absent makes Zapier's
    field mapping unstable and can error a downstream step.
  */
  const WEBHOOK_FIELDS = {
    company: 'company_name',
    companyDesc: 'company_description',
    website: 'business_website',
    category: 'business_category',
    services: 'products_services',
    deck: 'pitch_deck',
    name: 'applicant_name',
    email: 'applicant_email',
    phone: 'phone',
  };

  // One id per submission, written into three places: the Shopify email, the
  // webhook payload, and the Calendly link. It is the only thing that ties an
  // application to the booking that follows, since the applicant can edit
  // their name and email on the Calendly screen.
  function makeSubmissionId() {
    const now = new Date();
    const stamp = now.toISOString().slice(0, 10).replace(/-/g, '');
    let suffix;
    try {
      suffix = window.crypto.randomUUID().slice(0, 8);
    } catch (error) {
      suffix = Math.random().toString(36).slice(2, 10);
    }
    return 'ref-' + stamp + '-' + suffix;
  }

  // How long to hold the page before giving up on the webhook and letting the
  // form post anyway. The applicant must never be stranded by a slow endpoint.
  const WEBHOOK_TIMEOUT = 1500;

  function getEndpoint(form) {
    const holder = form.closest('[data-referral-webhook]');
    const fromHolder = holder && holder.dataset.referralWebhook;
    return fromHolder || form.dataset.webhook || '';
  }

  function sendWebhook(endpoint, form, submissionId, pageParams) {

    const body = {
      submission_id: submissionId,
      submitted_at: new Date().toISOString(),
      page_url: window.location.href,
      referrer: document.referrer || '',
    };

    Object.keys(WEBHOOK_FIELDS).forEach((key) => {
      const field = form.querySelector('[data-field="' + key + '"]');
      body[WEBHOOK_FIELDS[key]] = field ? field.value.trim() : '';
    });

    UTM_KEYS.forEach((key) => {
      body[key] = pageParams.get(key) || '';
    });

    /*
      Sent as form-urlencoded, and with no custom headers, for one specific
      reason: those are the conditions for a CORS "simple" request.

      An `application/json` body makes the request non-simple, so the browser
      sends a preflight OPTIONS first. Zapier's catch hook answers that
      preflight with only `access-control-allow-origin` and omits
      `access-control-allow-headers`, so the preflight fails and the browser
      blocks the real POST. The OPTIONS still reaches Zapier, which logs it as
      an inbound request with no body -- an empty record, every time, while
      curl to the same URL works perfectly because curl never preflights.

      A URLSearchParams body makes the browser set
      `application/x-www-form-urlencoded` itself, which is one of the three
      safelisted content types. Do not add headers here: any single custom
      header brings the preflight back and breaks this again.

      `keepalive` is kept as belt and braces, but the caller also waits for
      this promise before navigating, so delivery no longer depends on it.
    */
    try {
      return window.fetch(endpoint, {
        method: 'POST',
        body: new URLSearchParams(body),
        keepalive: true,
      });
    } catch (error) {
      return Promise.resolve();
    }
  }

  // Long-ish free text still has to fit in a URL. The full answers are always
  // in the notification email, so trimming here loses nothing important.
  const MAX_ANSWER = 400;
  const URL_BUDGET = 1800;
  const REDIRECT_DELAY = 2500;

  function readStash() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  function buildBookingUrl(base) {
    let url;
    try {
      url = new URL(base);
    } catch (error) {
      // Refuse to redirect to something we could not parse rather than
      // sending the applicant to a relative 404.
      return null;
    }

    // A hardcoded month= pins the calendar to a month that is usually in the
    // past by the time anyone clicks. Always let Calendly pick the month.
    url.searchParams.delete('month');

    const stash = readStash();
    if (stash.name) url.searchParams.set('name', stash.name);
    if (stash.email) url.searchParams.set('email', stash.email);

    // Carry the application itself into the booking, so the answers arrive on
    // the calendar invite instead of only in a separate email.
    function writeAnswers(limit) {
      ANSWER_FIELDS.forEach((key, index) => {
        const value = (stash[key] || '').trim();
        if (!value) return;
        const clipped = value.length > limit ? value.slice(0, limit) + '…' : value;
        url.searchParams.set('a' + (index + ANSWER_OFFSET), clipped);
      });
    }

    writeAnswers(MAX_ANSWER);

    // Halve the per-answer allowance until the whole URL is comfortably inside
    // the ~2000 character ceiling that older proxies enforce.
    let limit = MAX_ANSWER;
    while (url.toString().length > URL_BUDGET && limit > 40) {
      limit = Math.floor(limit / 2);
      writeAnswers(limit);
    }

    const pageParams = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((key) => {
      const value = pageParams.get(key) || stash[key];
      if (value) url.searchParams.set(key, value);
    });

    /*
      Calendly records only its five reserved UTM parameters, so the submission
      id has to travel inside one of them to reach the booking. utm_term is
      used only when the visitor did not arrive carrying one of their own:
      overwriting a real campaign value to smuggle a tracking id would trade
      away attribution that matters more than the join key.
    */
    if (stash.submissionId && !url.searchParams.get('utm_term')) {
      url.searchParams.set('utm_term', stash.submissionId);
    }

    return url.toString();
  }

  function initForm(form) {
    initUrlFields(form);

    const honeypot = form.querySelector('[data-honeypot]');
    const submit = form.querySelector('[data-referral-submit]');

    form.addEventListener('submit', (event) => {
      if (honeypot && honeypot.value !== '') {
        event.preventDefault();
        return;
      }

      // Native validation runs first; only lock the button once the browser
      // is actually going to post, or a failed submit would strand the user.
      if (form.checkValidity && !form.checkValidity()) return;

      if (submit) {
        submit.setAttribute('aria-disabled', 'true');
        submit.setAttribute('aria-busy', 'true');
        // pointer-events does not stop a keyboard Enter on a focused button,
        // so the real disabled attribute is needed. Deferred one tick because
        // disabling during the submit event can cancel the submission.
        window.setTimeout(() => {
          submit.disabled = true;
        }, 0);
      }

      const pageParams = new URLSearchParams(window.location.search);
      const submissionId = makeSubmissionId();

      // Carry the id into the Shopify notification email too, so the inbox
      // copy and the webhook record can be matched later.
      const idField = form.querySelector('[data-submission-id]');
      if (idField) idField.value = submissionId;

      const payload = { submissionId: submissionId };
      STASH_FIELDS.forEach((key) => {
        payload[key] = form.querySelector('[data-field="' + key + '"]')?.value || '';
      });

      UTM_KEYS.forEach((key) => {
        const value = pageParams.get(key);
        if (value) payload[key] = value;
      });

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        sessionStorage.removeItem(HANDOFF_KEY);
      } catch (error) {
        /* Private browsing; the booking still works, just without prefill. */
      }

      const endpoint = getEndpoint(form);
      if (!endpoint) return;

      // Hold the navigation just long enough for the request body to leave the
      // browser, then submit for real. form.submit() does not re-fire this
      // listener, so there is no loop.
      event.preventDefault();

      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        form.submit();
      };

      window.setTimeout(release, WEBHOOK_TIMEOUT);
      sendWebhook(endpoint, form, submissionId, pageParams).then(release, release);
    });
  }

  function markHandedOff() {
    try {
      sessionStorage.setItem(HANDOFF_KEY, '1');
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* Private browsing. The visitor simply gets the redirect again. */
    }
  }

  function initRedirect(node) {
    const target = buildBookingUrl(node.dataset.bookingUrl);
    if (!target) return;
    const manualLink = node.querySelector('[data-booking-link]');
    const cancel = node.querySelector('[data-booking-cancel]');

    if (manualLink) manualLink.href = target;

    // One-shot: if we have already handed off once, this is the visitor
    // coming back from Calendly. Leave them here.
    let alreadyHandedOff = false;
    try {
      alreadyHandedOff = sessionStorage.getItem(HANDOFF_KEY) === '1';
    } catch (error) {
      /* no-op */
    }
    if (alreadyHandedOff) return;

    if (cancel) cancel.hidden = false;

    const timer = window.setTimeout(() => {
      markHandedOff();
      window.location.href = target;
    }, REDIRECT_DELAY);

    // Going now by hand still counts as the handoff.
    manualLink?.addEventListener('click', markHandedOff);

    // So does deciding not to go: a reload should not bounce them forward.
    cancel?.addEventListener('click', () => {
      window.clearTimeout(timer);
      markHandedOff();
      cancel.hidden = true;
    });
  }

  function initErrors(node) {
    // Shopify re-renders the whole page on a validation failure, so without
    // this the visitor lands at the top of a nine-field form with no idea
    // which field is wrong.
    node.focus();
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function init() {
    document.querySelectorAll('[data-referral-form]').forEach(initForm);
    document.querySelectorAll('[data-referral-redirect]').forEach(initRedirect);
    document.querySelectorAll('[data-referral-errors]').forEach(initErrors);
  }

  // A back-navigation restores the page from bfcache with the button still
  // disabled, which would leave the form permanently unsubmittable.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-referral-submit]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
